import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { companyDashboard } from '../../api/company/client'
import { format } from 'date-fns'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from 'recharts'

export default function CompanyReports() {
  const { t } = useTranslation('portal')
  const { data: overview, isLoading } = useQuery({
    queryKey: ['company', 'dashboard'],
    queryFn: () => companyDashboard.overview().then((r) => r.data),
  })

  const chartData = overview?.daily_trend ?? []
  const periodLabel = overview
    ? `${format(new Date(overview.period_start), 'MMM d')} – ${format(new Date(overview.period_end), 'MMM d, yyyy')}`
    : ''

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">{t('company.reports.title')}</h1>

      <div className="rounded-xl bg-white border border-gray-200 p-6 mb-6 shadow-sm">
        <h2 className="text-lg font-medium text-gray-900 mb-4">{t('company.reports.periodSummary')}</h2>
        {isLoading ? (
          <div className="h-32 flex items-center text-gray-500">{t('company.reports.loading')}</div>
        ) : overview ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-gray-500 text-sm">{t('company.reports.period')}</p>
              <p className="text-gray-900 text-sm">{periodLabel}</p>
            </div>
            <div>
              <p className="text-gray-500 text-sm">{t('company.reports.transactions')}</p>
              <p className="text-gray-900">{overview.total_transactions}</p>
            </div>
            <div>
              <p className="text-gray-500 text-sm">{t('company.reports.revenueBam')}</p>
              <p className="text-gray-900">{Number(overview.total_revenue).toFixed(2)}</p>
            </div>
            <div>
              <p className="text-gray-500 text-sm">{t('company.reports.netBam')}</p>
              <p className="text-blue-600 font-semibold">
                {Number(overview.net_amount).toFixed(2)}
              </p>
            </div>
            <div>
              <p className="text-gray-500 text-sm">{t('company.reports.avgTransactionBam')}</p>
              <p className="text-gray-900">
                {Number(overview.average_transaction_value).toFixed(2)}
              </p>
            </div>
          </div>
        ) : null}
      </div>

      <div className="rounded-xl bg-white border border-gray-200 p-6 shadow-sm">
        <h2 className="text-lg font-medium text-gray-900 mb-4">{t('company.reports.revenueVolumeChart')}</h2>
        {chartData.length > 0 ? (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="date"
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                  tickFormatter={(v) => format(new Date(v), 'MMM d')}
                />
                <YAxis
                  yAxisId="left"
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                  tickFormatter={(v) => `${v}`}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                  tickFormatter={(v) => `${v} BAM`}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb' }}
                  labelFormatter={(v) => format(new Date(v), 'PPP')}
                  formatter={(value: number, name: string) => [
                    name === 'volume' ? `${Number(value).toFixed(2)} BAM` : value,
                    name === 'volume' ? t('company.reports.chartVolumeBam') : t('company.reports.chartTransactions'),
                  ]}
                />
                <Legend />
                <Bar
                  yAxisId="left"
                  dataKey="transactions"
                  fill="#6b7280"
                  name={t('company.reports.chartTransactions')}
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  yAxisId="right"
                  dataKey="volume"
                  fill="#2563eb"
                  name={t('company.reports.chartVolumeBam')}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-gray-500 py-8">{t('company.reports.noDataPeriod')}</p>
        )}
      </div>
    </div>
  )
}
